import React, { useState } from 'react';
import { ArrowDownToLine, FileSpreadsheet } from 'lucide-react';
import {
  DemoState,
  INVOICE_LABELS,
  Trip,
  dateTime,
  duration,
  exportCsv,
  metrics,
  money,
  sum,
  hasTripData,
} from './model';
import { MetricCard } from './indicators';
import { ColumnHint } from './data-hint';

export default function Reports({
  state,
  rows,
  onSelect,
}: {
  state: DemoState;
  rows: Trip[];
  onSelect: (trip: Trip) => void;
}) {
  const [payOnly, setPayOnly] = useState(true);
  const [approval, setApproval] = useState('all');
  const [page, setPage] = useState(0);
  const invoiceFor = (trip: Trip) =>
    state.invoices.find((invoice) => invoice.tripIds.includes(trip.id));
  const trips = rows.filter(
    (trip) =>
      (!payOnly || metrics(trip).cents > 0) &&
      (approval === 'all' ||
        (approval === 'unbilled'
          ? !invoiceFor(trip)
          : invoiceFor(trip)?.status === approval))
  );
  const maxPage = Math.max(0, Math.ceil(trips.length / 20) - 1);
  const currentPage = Math.min(page, maxPage);
  function exportReport() {
    exportCsv(
      'relatorio-estadias.csv',
      [
        [
          'Viagem',
          'Placa',
          'Unidade',
          'Data agendamento',
          'Transportadora',
          'REC',
          'EXP',
          'Permanência total',
          'Horas adicionais',
          'Tarifa R$/hora',
          'Valor estadia',
          'Status aprovação',
          'Fatura',
          'Classificação NF',
          'CNPJ NF',
          'Logradouro NF',
        ],
        ...trips.map((trip) => {
          const value = metrics(trip);
          const invoice = invoiceFor(trip);
          return [
            trip.sourceId || trip.id,
            trip.plate,
            trip.unit,
            dateTime(trip.events.scheduled),
            trip.carrier,
            duration(value.rec),
            duration(value.exp),
            duration(value.total),
            duration(value.excess),
            '46,62',
            money(value.cents),
            invoice ? INVOICE_LABELS[invoice.status] : 'Ainda não faturada',
            invoice?.id || '',
            trip.site,
            trip.cnpj,
            trip.address,
          ];
        }),
      ],
      trips
    );
  }
  return (
    <>
      <div className="stay-section-label">
        <FileSpreadsheet size={19} />
        <div>
          <h2>Relatórios de estadia</h2>
          <p>Custos, aprovações e horas de parada por viagem.</p>
        </div>
      </div>
      <div className="stay-stat-grid">
        <MetricCard
          title="Viagens no relatório"
          value={trips.length}
          detail="Seleção atual, incluindo os filtros desta aba"
        />
        <MetricCard
          title="Horas adicionais elegíveis"
          value={duration(sum(trips.map((trip) => metrics(trip).excess)))}
          detail="Apuração por hora e minuto"
        />
        <MetricCard
          title="Valor de estadia"
          value={money(sum(trips.map((trip) => metrics(trip).cents)))}
          detail="R$ 46,62 por hora adicional"
          tone="amber"
        />
        <MetricCard
          title="Custo aprovado"
          value={money(
            sum(
              trips
                .filter((trip) => invoiceFor(trip)?.status === 'approved')
                .map((trip) => metrics(trip).cents)
            )
          )}
          detail="Valores com aprovação Corteva"
          tone="green"
        />
      </div>
      <section className="stay-card">
        <div className="stay-toolbar">
          <label>
            <input
              type="checkbox"
              checked={payOnly}
              onChange={(event) => {
                setPayOnly(event.target.checked);
                setPage(0);
              }}
            />{' '}
            Somente viagens com valor de estadia a pagar
          </label>
          <select
            aria-label="Aprovação no relatório"
            value={approval}
            onChange={(event) => {
              setApproval(event.target.value);
              setPage(0);
            }}
          >
            <option value="all">Todas as aprovações</option>
            <option value="unbilled">Ainda não faturada</option>
            {Object.entries(INVOICE_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
          <button onClick={exportReport}>
            <ArrowDownToLine size={15} /> Exportar relatório CSV
          </button>
        </div>
        <div className="stay-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Viagem / veículo</th>
                <th>
                  {rows.some((trip) => hasTripData(trip, 'Unidade'))
                    ? 'Local / data'
                    : 'Agendamento'}
                </th>
                <th>Transportadora</th>
                <th>
                  <ColumnHint
                    label="REC"
                    description="Tempo de recebimento e descarga, contado até a ocorrência de material entregue."
                  />
                </th>
                <th>
                  <ColumnHint
                    label="EXP"
                    description="Tempo de expedição, do início do carregamento até a conclusão da carga."
                  />
                </th>
                <th>
                  <ColumnHint
                    label="Total"
                    description="Tempo total de permanência apurado pela soma REC + EXP. O intervalo de espera não é somado."
                  />
                </th>
                <>
                  <th>
                    <ColumnHint
                      label="Horas adicionais"
                      description="Tempo elegível que excedeu as 10 horas de franquia. Viagens com chegada atrasada não geram cobrança."
                    />
                  </th>
                  <th>
                    <ColumnHint
                      label="Valor"
                      description="Horas e minutos adicionais multiplicados pela tarifa de R$ 46,62 por hora, arredondados para centavos por viagem."
                    />
                  </th>
                  <th>Aprovação</th>
                  <th>Fatura</th>
                </>
              </tr>
            </thead>
            <tbody>
              {trips
                .slice(currentPage * 20, currentPage * 20 + 20)
                .map((trip) => {
                  const value = metrics(trip);
                  const invoice = invoiceFor(trip);
                  return (
                    <tr key={trip.id}>
                      <td>
                        <button
                          className="stay-link"
                          onClick={() => onSelect(trip)}
                        >
                          {trip.plate}
                          <small>{trip.sourceId || trip.id}</small>
                        </button>
                      </td>
                      <td>
                        {hasTripData(trip, 'Unidade') && trip.unit}
                        <small>{dateTime(trip.events.scheduled)}</small>
                      </td>
                      <td>{trip.carrier}</td>
                      <td>{duration(value.rec)}</td>
                      <td>{duration(value.exp)}</td>
                      <td>{duration(value.total)}</td>
                      <>
                        <td>{duration(value.excess)}</td>
                        <td>{money(value.cents)}</td>
                        <td>
                          {invoice
                            ? INVOICE_LABELS[invoice.status]
                            : 'Ainda não faturada'}
                        </td>
                        <td>{invoice?.id || '—'}</td>
                      </>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
        {!trips.length && (
          <div className="stay-empty-state">
            Nenhuma viagem neste relatório. Ajuste os filtros.
          </div>
        )}
        <div className="stay-pagination">
          <span>
            {trips.length} viagens · página {currentPage + 1} de {maxPage + 1}
          </span>
          <button
            disabled={currentPage === 0}
            onClick={() => setPage(currentPage - 1)}
          >
            Anterior
          </button>
          <button
            disabled={currentPage === maxPage}
            onClick={() => setPage(currentPage + 1)}
          >
            Próxima
          </button>
        </div>
      </section>
    </>
  );
}
